import { CardSchemeId, IntegrationSystem, OrderChannel } from '@biteinc/enums';
import { UniversalPaymentHelper } from '@biteinc/helpers-universal';

namespace StoredValueHelper {
  export const freedomPayStoredValueCardSchemeIdStorageKey = 'freedomPayStoredValueCardSchemeId';

  const storedValueLikeCampusCardSystems = [
    IntegrationSystem.AtriumCampusCard,
    IntegrationSystem.CbordCampusCard,
    IntegrationSystem.QuickchargeCampusCard,
  ] as const satisfies readonly IntegrationSystem.CampusCard[];

  export type StoredValueLikeCampusCardSystem = (typeof storedValueLikeCampusCardSystems)[number];
  export type StoredValueLikeSystem =
    | IntegrationSystem.StoredValue
    | StoredValueLikeCampusCardSystem;

  export function isStoredValueLikeCampusCardSystem(
    campusCardSystem: IntegrationSystem | undefined,
  ): campusCardSystem is StoredValueLikeCampusCardSystem {
    return (
      !!campusCardSystem &&
      storedValueLikeCampusCardSystems.includes(campusCardSystem as StoredValueLikeCampusCardSystem)
    );
  }

  // Returns true if the gift card is actually some sort of secondary payment method, like a
  // student card or some other stored payment method.
  /**
   * Auth tokens can end in base64 padding ("=="); show the last four meaningful
   * characters instead.
   */
  export function lastFourFromToken(token: string): string {
    const alphanumeric = token.replace(/[^a-zA-Z0-9]/g, '');
    return alphanumeric.slice(-4) || token.slice(-4);
  }

  export function isStoredValueSecondaryPayment(): boolean {
    return !!gcn.location.getStoredValueIntegration()?.useAsSecondaryPayment;
  }

  export function requiresNativeForStoredValue(
    storedValueSystem: StoredValueLikeSystem | undefined,
  ): boolean {
    if (!storedValueSystem) {
      return false;
    }

    switch (storedValueSystem) {
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.WorldpayStoredValue:
        return true;
      case IntegrationSystem.AtriumCampusCard:
      case IntegrationSystem.AtriumStoredValue:
        // On the kiosk a physical card needs to be swiped because students might know each other's
        // student IDs (and hence Atrium card numbers)
        if (
          [OrderChannel.DriveThru, OrderChannel.Kiosk].includes(gcn.location.get('orderChannel'))
        ) {
          return true;
        }
        // On a linebuster, a staff member is entering the number so we will only use a native
        // reader if we have one connected to the kiosk.
        return !!gcn.kiosk?.get('hasMsr');
      case IntegrationSystem.FreedomPayStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.QuickchargeCampusCard:
        if (gcn.kiosk?.get('hasHid') || gcn.kiosk?.get('hasMsr')) {
          return true;
        }
        return false;
    }
  }

  export function getStoredValueSsoUrl(
    storedValueSystem: StoredValueLikeSystem,
    authUrl: string,
    redirectUrl: string,
  ): string {
    switch (storedValueSystem) {
      case IntegrationSystem.AtriumCampusCard:
      case IntegrationSystem.AtriumStoredValue:
        return `${authUrl}&partner=bite&url=${redirectUrl}`;
      case IntegrationSystem.CbordCampusCard:
        return `${authUrl}&redirecturl=${redirectUrl}`;
      case IntegrationSystem.FreedomPayStoredValue: {
        const url = new URL(authUrl);
        url.searchParams.set('redirecturl', redirectUrl);
        return url.toString();
      }
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue:
        throw new Error(`${storedValueSystem} does not support SSO authentication`);
    }
  }

  export function getMultipleAccountStoredValueSsoAuthUrl(
    storedValueSystem: StoredValueLikeSystem,
    authUrl: string,
  ): string {
    switch (storedValueSystem) {
      case IntegrationSystem.FreedomPayStoredValue:
        return getFreedomPayMultipleAccountSsoAuthUrl(authUrl);
      case IntegrationSystem.AtriumCampusCard:
      case IntegrationSystem.AtriumStoredValue:
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue:
        return authUrl;
    }
  }

  function getFreedomPayMultipleAccountSsoAuthUrl(authUrl: string): string {
    try {
      const url = new URL(authUrl);
      const pathParts = url.pathname.split('/');
      const authPage = pathParts[pathParts.length - 1];
      if (!authPage || authPage.startsWith('mlogin') || !authPage.startsWith('login')) {
        return authUrl;
      }
      pathParts[pathParts.length - 1] = `m${authPage}`;
      url.pathname = pathParts.join('/');
      return url.toString();
    } catch {
      return authUrl;
    }
  }

  export function storedValueSupportsSavedCards(storedValueSystem: StoredValueLikeSystem): boolean {
    switch (storedValueSystem) {
      case IntegrationSystem.AtriumCampusCard:
      case IntegrationSystem.AtriumStoredValue:
      case IntegrationSystem.CbordCampusCard:
      case IntegrationSystem.FreedomPayStoredValue:
        return true;
      case IntegrationSystem.Archer:
      case IntegrationSystem.AurusPayStoredValue:
      case IntegrationSystem.ChaseStoredValue:
      case IntegrationSystem.EigenStoredValue:
      case IntegrationSystem.FreedomPayKioskStoredValue:
      case IntegrationSystem.Givex:
      case IntegrationSystem.OloStoredValue:
      case IntegrationSystem.ParPayStoredValue:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PinataPayNativeStoredValue:
      case IntegrationSystem.PinataPayStoredValue:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.QuickchargeCampusCard:
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.Tcc:
      case IntegrationSystem.Valutec:
      case IntegrationSystem.WorldpayStoredValue:
        return false;
    }
  }

  function isFreedomPayStoredValueCardSchemeId(cardSchemeId: CardSchemeId | undefined): boolean {
    return (
      cardSchemeId === CardSchemeId.FreedomPayStoredValue ||
      cardSchemeId === CardSchemeId.FreedomPayVoucher
    );
  }

  function getBalanceLimitedStoredValueSystem(
    storedValueSystem: StoredValueLikeSystem | undefined,
    cardSchemeId: CardSchemeId | undefined,
  ): IntegrationSystem.StoredValue | undefined {
    if (isFreedomPayStoredValueCardSchemeId(cardSchemeId)) {
      return IntegrationSystem.FreedomPayStoredValue;
    }

    if (isStoredValueLikeCampusCardSystem(storedValueSystem)) {
      return undefined;
    }

    return storedValueSystem;
  }

  // Returns false when the location's stored-value or campus-card integration can spend without a
  // known positive balance. CBORD has explicit zero-balance account types. FPSV production can also
  // approve an inquiry without returning accountBalance, unlike sandbox, so saved FPSV cards must
  // not treat a cached zero as a hard insufficient-balance result.
  export function balanceLimitsStoredValueOrCampusCardSpend(
    storedValueSystem: StoredValueLikeSystem | undefined,
    campusCardSystem: IntegrationSystem.CampusCard | undefined,
    accountType: number | string | undefined,
    cardSchemeId?: CardSchemeId,
  ): boolean {
    return (
      UniversalPaymentHelper.balanceLimitsStoredValueSpend(
        getBalanceLimitedStoredValueSystem(storedValueSystem, cardSchemeId),
      ) && UniversalPaymentHelper.balanceLimitsCampusCardSpend(campusCardSystem, accountType)
    );
  }

  export function cachedBalanceCanDisableStoredValueSpend(
    storedValueSystem: StoredValueLikeSystem | undefined,
    campusCardSystem: IntegrationSystem.CampusCard | undefined,
    accountType: number | string | undefined,
    cardSchemeId?: CardSchemeId,
  ): boolean {
    return (
      balanceLimitsStoredValueOrCampusCardSpend(
        storedValueSystem,
        campusCardSystem,
        accountType,
        cardSchemeId,
      ) && !isFreedomPayStoredValueCardSchemeId(cardSchemeId)
    );
  }
}

export default StoredValueHelper;
